import { BRAND } from "@/lib/api";

// Editorial categories. Add new ones freely — the blog UI derives filters from articles.
export const BLOG_CATEGORIES = [
  "All",
  "Preshh News",
  "Events",
  "Partners",
  "Products",
  "Industry",
  "Behind the Scenes",
];

// To publish a new article, add an object to this array. Newest first.
// Content is a list of blocks: {type: paragraph|heading|image|quote|cta}.
export const ARTICLES = [
  {
    slug: "preshh-bcb-berlin-2026",
    category: "Events",
    featured: true,
    title: "Preshh Is Coming to BCB Berlin for the First Time",
    subtitle:
      "From Brazil to Europe: Preshh is bringing its approach to carbonation, cocktails and beverage innovation to one of the world's leading bar industry gatherings.",
    excerpt:
      "For the first time, Preshh will exhibit at Bar Convent Berlin, 12–14 October 2026 at Messe Berlin — meeting the international community shaping the future of hospitality.",
    author: "Preshh",
    date: "2026-06-15",
    dateLabel: "15 June 2026",
    hero: "/blog/bcb-hero.jpg",
    heroAlt: "A premium international bar and beverage trade show hall filled with modern exhibition stands and hospitality professionals",
    seoTitle: "Preshh at BCB Berlin 2026 | Bar Convent Berlin",
    seoDescription:
      "Preshh is heading to Berlin for the first time. Meet us at Bar Convent Berlin, 12–14 October 2026, and discover a new approach to carbonation, cocktails and beverage innovation.",
    keywords: [
      "Preshh", "BCB Berlin 2026", "Bar Convent Berlin", "carbonation",
      "cocktail innovation", "beverage technology", "bar technology",
      "hospitality innovation", "professional carbonation",
    ],
    content: [
      { type: "paragraph", text: "Preshh is heading to Berlin." },
      { type: "paragraph", text: "For the first time, Preshh will be exhibiting at Bar Convent Berlin, taking place from 12–14 October 2026 at Messe Berlin." },
      { type: "paragraph", text: "For us, this is more than another event on the calendar. It is an opportunity to bring Preshh into the heart of an international community of bartenders, bar owners, distributors and beverage professionals — and to meet the people who are shaping what comes next." },
      { type: "heading", text: "Why BCB matters" },
      { type: "paragraph", text: "Bar Convent Berlin is one of the world's leading trade shows for the bar and beverage industry, bringing together professionals from around 90 countries. It is a major international meeting point where ideas, techniques and the future of hospitality are exchanged over three intense days." },
      { type: "image", src: BRAND.open, alt: "The Preshh GO portable carbonation system", caption: "Preshh GO — portable carbonation, built for professionals." },
      { type: "heading", text: "What Preshh is about" },
      { type: "paragraph", text: "Preshh was built around a simple idea: pressure can be more than carbonation. It can be a tool for creativity, consistency and better service." },
      { type: "paragraph", text: "From cocktails and spritzes to sodas, infusions, macerations and high-volume beverage operations, Preshh gives professionals greater control over how beverages are created, carbonated and served." },
      { type: "image", src: "/whoweare/aperol.jpg", alt: "A sparkling Preshh cocktail served over ice", caption: "From spritzes to sodas — consistency, glass after glass." },
      { type: "heading", text: "An international journey" },
      { type: "paragraph", text: "Over the years, Preshh has evolved through real-world use — from bars and restaurants to large-scale activations — learning directly from the people who use the system behind the bar." },
      { type: "paragraph", text: "BCB is an opportunity to continue that conversation on an international stage. We're coming to Berlin to demonstrate what Preshh can do, meet future partners and distributors, exchange ideas with bartenders and hospitality professionals, and, most importantly, listen." },
      { type: "quote", text: "Preshh has always been built around people — people who create, serve, experiment and constantly look for a better way to do things." },
      { type: "image", src: "/blog/bcb-closing.jpg", alt: "A bartender pouring a cocktail at a modern bar counter", caption: "See you in Berlin." },
      { type: "heading", text: "See you in Berlin" },
      { type: "paragraph", text: "12–14 October 2026 · Messe Berlin · Bar Convent Berlin" },
      { type: "cta", label: "Meet Preshh at BCB", href: "/contact", secondaryLabel: "Visit BCB", secondaryHref: "https://www.barconvent.com/en/" },
    ],
  },
  {
    slug: "preshh-on-the-world-class-stage",
    category: "Industry",
    featured: false,
    title: "Preshh on the World Class Stage",
    subtitle: "How a Brazilian idea earned its place among the world's best bartenders.",
    excerpt:
      "Preshh has shared the room with competitors from dozens of countries at World Class Brazil — a stage where precision, creativity and consistency are everything.",
    author: "Preshh",
    date: "2023-06-01",
    dateLabel: "2023",
    hero: "/whoweare/worldclass/wc01.jpg",
    heroAlt: "Bartenders competing on a professional cocktail competition stage",
    seoTitle: "Preshh on the World Class Stage | Preshh Blog",
    seoDescription:
      "Preshh has shared the World Class Brazil stage with competitors from dozens of countries — where precision, creativity and consistency define the best.",
    keywords: ["Preshh", "World Class", "bartending", "cocktail competition", "carbonation"],
    content: [
      { type: "paragraph", text: "In 2023, World Class brought together the most talented bartenders on the planet, with competitors representing dozens of countries. It is a stage where precision, creativity and consistency are everything." },
      { type: "paragraph", text: "It is exactly the environment Preshh was made for. When every serve counts, controllable pressure becomes a tool for repeatable, professional results — spritz after spritz, soda after soda." },
      { type: "image", src: "/whoweare/aperol.jpg", alt: "A carbonated Preshh cocktail", caption: "Repeatable results, under pressure." },
      { type: "paragraph", text: "We're proud of where Preshh comes from, and of the professionals who put it to work at the highest level of the craft." },
    ],
  },
  {
    slug: "100000-cocktails-in-one-week",
    category: "Behind the Scenes",
    featured: false,
    title: "100,000 Cocktails in One Week",
    subtitle: "What Carnival in Salvador taught us about beverage operations at scale.",
    excerpt:
      "During Carnival in Salvador, the Purgatório operation served more than 100,000 cocktails in a single week — a real-world test of speed, consistency and pressure.",
    author: "Preshh",
    date: "2026-04-10",
    dateLabel: "10 April 2026",
    hero: "/whoweare/carnival.jpg",
    heroAlt: "A high-energy Carnival crowd in Salvador, Brazil",
    seoTitle: "100,000 Cocktails in One Week | Preshh Blog",
    seoDescription:
      "During Carnival in Salvador, the Purgatório operation served more than 100,000 cocktails in a single week — a real-world test of beverage operations at scale.",
    keywords: ["Preshh", "Carnival", "Salvador", "high volume bar", "beverage operations"],
    content: [
      { type: "paragraph", text: "Few environments test a beverage operation like Carnival in Salvador. During one edition, the Purgatório operation served more than 100,000 cocktails across the week — with peaks of roughly 24,000 in a single day." },
      { type: "paragraph", text: "At that scale, there is no room for inconsistency or bottlenecks. Every second and every serve matters." },
      { type: "quote", text: "Volume is the ultimate stress test — and the best teacher." },
      { type: "paragraph", text: "Experiences like these shape how we think about Preshh: built for the real world, refined by the people who work at the sharp end of hospitality." },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
export const getFeatured = () => ARTICLES.find((a) => a.featured) || ARTICLES[0];
export const getRelated = (slug, limit = 3) =>
  ARTICLES.filter((a) => a.slug !== slug).slice(0, limit);
